import { Grid } from '@mui/material'
import { Formik } from 'formik'
import { isEmpty } from 'lodash'
import { observer } from 'mobx-react-lite'
import React, { useEffect, useMemo, useState } from 'react'
import { getI18n, useTranslation } from 'react-i18next'
import { getErrMsg, toastNeg, toastPos } from '../../../common/toastHelper'
import { IDropdownProjects, IProjectPersonDetails, IUserProject } from '../../../interfaces/projects/IProjects'
import { IRoleProject } from '../../../interfaces/roles/IUserRoles'
import { ILinkedProject } from '../../../interfaces/skills/ISkills'
import { useStore } from '../../../Provider'
import { ExitConfirmation } from '../../OPcomponents/ExitConfirmation'
import { OPLoadingCircle } from '../../OPcomponents/OPLoadingcircle'
import { OPSkillsCombobox } from '../../OPcomponents/OPSkillsCombobox'
import '../../styles/grid.scss'
import { OPSkillsButtons } from '../OPSkillsButtons'
import '../profile.scss'

const ProjectRoleLink = (props: {
  setLinkingProject?: (val: boolean) => void
  setLinkedProjects: (projects: ILinkedProject[]) => void
  editCurrentRole: any
  linkedProjects: ILinkedProject[]
}): JSX.Element => {
  const { setLinkingProject, setLinkedProjects, editCurrentRole, linkedProjects } = props
  const rootStore = useStore()
  const { t } = useTranslation()
  const { validatedUser } = rootStore.loginStore
  const { postNewRoleProject, setShowAddProjectToRoleModal } = rootStore.personStore

  const {
    showAddSkillToProjectModal: showAddRoleToProjectModal,
    projects,
    getDetailedUserProjects,
    clearUserProjectDetails,
    loadingProjectPersonDetails,
  } = rootStore.projectStore
  const { userObject, getUserOverview } = rootStore.personStore

  const [chosenDropdownProject, setChosenDropdownProject] = useState<IDropdownProjects>()
  const [selectedProject, setSelectedProject] = useState<IProjectPersonDetails>()

  useEffect(() => {
    return () => {
      clearUserProjectDetails()
    }
  }, [clearUserProjectDetails])

  useEffect(() => {
    return () => {
      clearUserProjectDetails()
      setShowAddProjectToRoleModal(false)
    }
  }, [clearUserProjectDetails, setShowAddProjectToRoleModal])

  useEffect(() => {
    const fetchOverview = async () => {
      if (isEmpty(userObject)) {
        await getUserOverview(validatedUser)
      }
    }
    fetchOverview()
  }, [getUserOverview, userObject, validatedUser])

  const filteredDropdownProjects = useMemo(() => {
    return projects
      .filter((proj) => Boolean(proj.userHas))
      .filter((i) => {
        // Check if project is already linked based on linkedProjects array
        const isAlreadyLinked = linkedProjects?.some((linkedProject) => Number(linkedProject.projectId) === Number(i.project.projectId))
        return !isAlreadyLinked
      })
      .map((project) => ({
        key: project.project.projectId,
        value: String(project.project.projectId),
        text: project.project.name + (project.project.customer ? ` (${project.project.customer})` : ''),
        isEmploymentProject: project.isEmployerJobTitlesProject,
      }))
      .sort((a, b) => {
        if (a.isEmploymentProject !== b.isEmploymentProject) {
          return a.isEmploymentProject ? -1 : 1
        }
        return a.text.localeCompare(b.text, getI18n().language, { sensitivity: 'base' })
      })
  }, [projects, linkedProjects])

  const initValues: IUserProject = {
    userId: validatedUser,
    manDays: selectedProject?.manDays ?? 0,
    manMonths: selectedProject?.manMonths ?? 0,
    projectId: selectedProject?.projectId ?? (chosenDropdownProject?.key || -1),
    startDate: selectedProject?.startDate,
    endDate: selectedProject?.endDate,
    usingManDays: selectedProject?.usingManDays ? true : false,
    usingManMonths: false,
    descriptionFi: selectedProject?.descriptionFi ?? '',
    descriptionEng: selectedProject?.descriptionEng ?? '',
    roleIds: [],
  }

  const submitForm = async (values: IUserProject) => {
    const newPersonRoleProject: IRoleProject = {
      userId: validatedUser,
      projectId: values.projectId,
      roleId: editCurrentRole.roleId,
    }

    try {
      await postNewRoleProject(newPersonRoleProject)

      // Manually add the new project to the linkedProjects array
      if (selectedProject) {
        const newLinkedProject: ILinkedProject = {
          projectId: selectedProject.projectId,
          projectName: selectedProject.projectName || 'Unknown',
          customer: selectedProject.customer || '',
          description: selectedProject.descriptionFi || selectedProject.descriptionEng || '',
          employerId: 0, // Default value - will be updated on refresh
          endDate: selectedProject.endDate || '',
          kuukaudet: selectedProject.manMonths || 0,
          manDays: selectedProject.manDays || 0,
          personId: validatedUser,
          personName: '', // Default value - will be updated on refresh
          projectOwner: selectedProject.projectOwner || 0,
          quota: 0, // Default value - will be updated on refresh
          startDate: selectedProject.startDate || '',
          usingManDays: selectedProject.usingManDays || false,
        }
        setLinkedProjects([...linkedProjects, newLinkedProject])
      }

      toastPos(t('Toasts.RoleProjectAdd'), true)
    } catch (err) {
      toastNeg(getErrMsg(err, t) || t('Toasts.RoleProjectAddError'), true)
    } finally {
      await handleClose()
    }
  }

  const handleClose = async () => {
    if (setLinkingProject) {
      setLinkingProject(false)
    }
    // Since we don't have a detailed endpoint, we'll refresh roles to get updated linked projects
    // For now, we'll just clear the form and let the parent component handle updates
    setChosenDropdownProject(undefined)
    setSelectedProject(undefined)
  }

  const onProjectChange = async (projectId: number) => {
    const changedProject = await getDetailedUserProjects(validatedUser, projectId)
    setSelectedProject(changedProject)
  }

  return (
    <Grid container>
      {loadingProjectPersonDetails ? (
        <Grid xs={12} item>
          <OPLoadingCircle />
        </Grid>
      ) : (
        <Formik enableReinitialize initialValues={initValues} onSubmit={(values) => submitForm(values)}>
          {({ setFieldValue, isSubmitting, handleSubmit, dirty }) => (
            <Grid container paddingBottom={2}>
              <ExitConfirmation when={dirty} />
              <Grid xs={12} item className='griditem--formitem__titlemargin'>
                <span className='myskill--font__titlefont sini'>{t('Roles.AddProject')}</span>
              </Grid>
              <Grid container rowSpacing={4} columnSpacing={4}>
                <Grid xs={12} lg={6} item>
                  <Grid container spacing={4}>
                    <Grid xs={12} item>
                      <OPSkillsCombobox
                        name='projectId'
                        label={`${t('Projects.EmployerOrProject')} *`}
                        optionList={filteredDropdownProjects}
                        curState={chosenDropdownProject}
                        groupBy={(opt) => (opt?.isEmploymentProject ? t('Projects.Employer') : t('Projects.Project'))}
                        onChange={(_e, val) => {
                          setChosenDropdownProject(val)
                          onProjectChange(val.value)
                        }}
                        onDelete={() => {
                          setChosenDropdownProject(undefined)
                          setFieldValue('projectId', null)
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <OPSkillsButtons
                  noPadding
                  bothDisabled={showAddRoleToProjectModal || isSubmitting || !chosenDropdownProject}
                  disabled={!chosenDropdownProject || showAddRoleToProjectModal || isSubmitting}
                  onClose={handleClose}
                  onSubmit={handleSubmit}
                />
              </Grid>
            </Grid>
          )}
        </Formik>
      )}
    </Grid>
  )
}

export default observer(ProjectRoleLink)
